import { createFileRoute, Link, getRouteApi } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, TrendingUp, Users, Handshake, MapPin } from "lucide-react";
import { Container, SectionEyebrow, GlassCard, CardMotion } from "@/components/site/Primitives";
import { CategoryPill } from "@/components/site/CategoryPill";
import { HeroSearch } from "@/components/site/HeroSearch";

import { EnquiryDialog } from "@/components/site/EnquiryDialog";
import { SectionRail } from "@/components/site/SectionRail";
import { ReadBioButton } from "@/components/site/LeaderBioDialog";
import { WhoWeServeSection } from "@/components/site/WhoWeServeSection";

import { loadPageBySlug, loadWhoWeServe } from "@/lib/content-loader";
import { insightDisplayLabel } from "@/lib/insights/categories";
import type { Article, ClientStory, ServiceCategory } from "@/lib/content";
import type { Leader } from "@/lib/types/content";
import { formatPublicDateShort } from "@/lib/format-public-date";
import { CraftRenderer } from "@/components/craft/CraftRenderer";
import { hasCraftContent } from "@/lib/craft/serialize";
import heroPenang from "@/assets/penang-skyline-sunset-hero.png.asset.json";
import heroWebp640 from "@/assets/penang-skyline-sunset-hero-640.webp?url";
import heroWebp1280 from "@/assets/penang-skyline-sunset-hero-1280.webp?url";
import heroWebp1920 from "@/assets/penang-skyline-sunset-hero-1920.webp?url";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { buildOpenGraphMeta, buildTwitterMeta, DEFAULT_HOME_TITLE, DEFAULT_SITE_DESCRIPTION } from "@/lib/seo/meta";

export const Route = createFileRoute("/")({
  loader: async () => ({
    cmsPage: await loadPageBySlug("home"),
    whoWeServe: await loadWhoWeServe(),
  }),
  head: ({ loaderData }) => {
    const useDefaultHero =
      !loaderData?.cmsPage ||
      !hasCraftContent(loaderData.cmsPage.craftState as string | object);
    const title = DEFAULT_HOME_TITLE;
    const description = DEFAULT_SITE_DESCRIPTION;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        ...buildOpenGraphMeta({ pathname: "/", title, description }),
        ...buildTwitterMeta({ title, description }),
      ],
      links: useDefaultHero
        ? [
            {
              rel: "preload",
              as: "image",
              href: heroWebp640,
              fetchPriority: "high",
              type: "image/webp",
              media: "(max-width: 768px)",
            },
            {
              rel: "preload",
              as: "image",
              href: heroWebp1280,
              fetchPriority: "high",
              type: "image/webp",
              media: "(min-width: 769px)",
            },
          ]
        : [],
    };
  },
  component: HomePage,
});

const ASK_PROMPTS = [
  "Ask about audit, tax, advisory…",
  "How do I file SST in Malaysia?",
  "Need help with transfer pricing?",
  "Read our latest insights…",
];

const TRENDING = [
  { label: "Audit", to: "/services/$slug", params: { slug: "audit-assurance" } },
  { label: "Tax", to: "/services/$slug", params: { slug: "tax-services" } },
  { label: "Advisory", to: "/services/$slug", params: { slug: "advisory" } },
] as const;

function formatDate(d: string) {
  return formatPublicDateShort(d);
}

const rootRoute = getRouteApi("__root__");

function HomePage() {
  const { cmsPage, whoWeServe } = Route.useLoaderData();
  const { megaMenu, searchData } = rootRoute.useLoaderData();
  const articles = megaMenu.insights as Article[];
  const clientStories = megaMenu.stories as ClientStory[];
  const serviceCategories = megaMenu.services as ServiceCategory[];
  const leaders = searchData.leaders as Leader[];
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  if (cmsPage && hasCraftContent(cmsPage.craftState as string | object)) {
    return (
      <>
        <CraftRenderer
          craftState={cmsPage.craftState}
          craftCustomCss={cmsPage.craftCustomCss}
        />
        <WhoWeServeSection items={whoWeServe} />
      </>
    );
  }

  return (
    <>
      <SectionRail
        sections={[
          { id: "about", label: "About" },
          { id: "industries", label: "Industries" },
          { id: "leadership", label: "Leadership" },
          { id: "insights", label: "Insights" },
          { id: "contact", label: "Contact" },
        ]}
      />

      {/* HERO — full-height image, left-aligned content per client mockup */}
      <section className="relative isolate flex min-h-[72vh] flex-col overflow-hidden">
        <picture className="absolute inset-0 -z-20">
          <source
            srcSet={`${heroWebp640} 640w, ${heroWebp1280} 1280w, ${heroWebp1920} 1920w`}
            sizes="100vw"
            type="image/webp"
          />
          <img
            src={heroPenang.url}
            alt="Penang skyline at sunset over George Town and the waterfront"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover object-[center_60%]"
          />
        </picture>
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-r from-primary/90 via-primary/55 to-primary/20"
        />

        <Container>
          <div className="flex min-h-[72vh] flex-col justify-center pb-10 pt-20 text-primary-foreground md:pb-12 md:pt-24">
            <div className="max-w-5xl">
              <h1 className="text-[2rem] font-bold leading-[1.1] tracking-tight text-primary-foreground sm:text-4xl md:text-5xl lg:text-6xl">
                <span className="block sm:whitespace-nowrap">Trusted Advisors for</span>
                <span className="block sm:whitespace-nowrap">Generations of Businesses</span>
                <span className="mt-2 block text-gold">Since 1978</span>
              </h1>

              <div className="mt-5">
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 text-base font-semibold text-white transition hover:text-gold"
                  >
                    Discover How We Can Help <ArrowRight className="h-4 w-4" />
                  </Link>
              </div>

              {/* Search bar */}
              <div className="mt-6 w-full max-w-xl">
                <HeroSearch size="large" variant="glass" rotatingPlaceholders={ASK_PROMPTS} resultsPosition="above" />
              </div>

              {/* Topic pills */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {TRENDING.map((t) => (
                  <Link
                    key={t.label}
                    to={t.to}
                    params={t.params}
                    className="rounded-full border border-white/30 bg-white/10 px-3.5 py-1.5 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
                  >
                    {t.label}
                  </Link>
                ))}
                <Link
                  to="/insights"
                  className="rounded-full border border-white/30 bg-white/10 px-3.5 py-1.5 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
                >
                  Insights
                </Link>
                <Link
                  to="/services"
                  className="rounded-full border border-white/30 bg-white/10 px-3.5 py-1.5 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
                >
                  Services
                </Link>
              </div>
            </div>



          </div>
        </Container>
      </section>

      <EnquiryDialog open={enquiryOpen} onOpenChange={setEnquiryOpen} />





      {/* ABOUT THE FIRM — pillars */}
      <section id="about" className="relative bg-primary py-10 md:py-12 text-primary-foreground">
        <Container>
          <h2 className="sr-only">About the firm</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4 md:gap-0 md:divide-x md:divide-white/15">
            {[
              {
                Icon: Users,
                title: "Decades of Excellence",
                body: "A legacy of integrity, independence and professional excellence since 1978.",
              },
              {
                Icon: Handshake,
                title: "Trusted by Generations",
                body: "Long-standing relationships built on trust, reliability and consistent value.",
              },
              {
                Icon: TrendingUp,
                title: "Solutions that Drive Growth",
                body: "Practical, forward-thinking advice that helps businesses grow with confidence.",
              },
              {
                Icon: MapPin,
                title: "Deep Roots. Local Insight.",
                body: "Proudly Penang-based with deep local roots and a strong understanding of businesses across Malaysia.",
              },
            ].map(({ Icon, title, body }) => (
              <Link
                key={title}
                to="/about"
                className="group block md:px-5"
              >
                <Icon className="h-6 w-6 text-[color:var(--gold)]" strokeWidth={1.5} />
                <h3 className="mt-3 text-base font-bold text-white transition group-hover:text-gold md:text-lg">
                  {title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-white/75">{body}</p>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative bg-stone/40 py-20 md:py-24">
        <Container>
          <h2 className="text-2xl font-semibold tracking-tight text-primary md:text-3xl lg:text-4xl">
            Our services
          </h2>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {serviceCategories.map((s, i) => (
              <Link key={s.slug} to="/services/$slug" params={{ slug: s.slug }} className="group h-full">
                <CardMotion
                  animateIndex={i}
                  className="flex h-full flex-col rounded-xl bg-card p-6 transition hover:shadow-[0_18px_44px_-22px_rgb(32_56_112/0.3)]"
                >
                  <h3 className="text-lg font-semibold tracking-tight text-primary">{s.name}</h3>
                  <p className="mt-2 flex-1 text-base leading-relaxed text-muted-foreground line-clamp-3">{s.short}</p>
                  <p className="mt-4 inline-flex items-center text-primary transition group-hover:translate-x-1">
                    <span className="sr-only">Explore services</span>
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </p>
                </CardMotion>
              </Link>
            ))}
          </div>

          <div className="mt-8">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-md border border-primary px-5 py-3 text-base font-semibold text-primary transition hover:bg-primary hover:text-primary-foreground"
            >
              Explore all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Container>
      </section>

      {/* INDUSTRIES — Who we serve (CMS with static fallback) */}
      <WhoWeServeSection items={whoWeServe} />

      {/* LEADERSHIP — simplified */}
      <section id="leadership" className="relative py-24 md:py-32">
        <Container>
          <h2 className="text-2xl font-semibold tracking-tight text-primary md:text-3xl lg:text-4xl">Leadership</h2>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-5 lg:gap-6">
            {leaders.map((l, i) => (
              <CardMotion
                key={l.slug}
                animateIndex={i}
                className="flex flex-col overflow-hidden rounded-xl bg-card transition"
              >
                <ResponsiveImage
                  src={l.image}
                  alt={`Portrait of ${l.name}`}
                  loading="lazy"
                  width={400}
                  height={500}
                  widths={[240, 480]}
                  sizes="(max-width: 640px) 50vw, 20vw"
                  className="aspect-[4/5] w-full object-cover"
                />
                <div className="flex flex-1 flex-col p-4 lg:p-5">
                  <h3 className="text-base font-semibold tracking-tight text-primary lg:text-lg">{l.name}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground line-clamp-2 lg:text-sm">{l.qualifications}</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-gold lg:text-sm">{l.designation}</p>
                  <div className="mt-auto pt-3">
                    <ReadBioButton leader={l} />
                  </div>
                </div>
              </CardMotion>
            ))}
          </div>
        </Container>
      </section>

      {/* CLIENT STORIES */}
      <section id="client-stories" className="relative py-16 md:py-20">
        <Container>
          <div className="flex items-end justify-between gap-6">
            <h2 className="text-2xl font-semibold tracking-tight text-primary md:text-3xl lg:text-4xl">
              Client Stories
            </h2>
            <Link
              to="/client-stories"
              className="hidden text-base font-semibold text-primary hover:underline md:inline-flex md:items-center md:gap-1"
            >
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {clientStories.slice(0, 3).map((s, i) => (
              <Link
                key={s.slug}
                to="/client-stories/$slug"
                params={{ slug: s.slug }}
                className="group h-full"
              >
                <GlassCard animateIndex={i} className="flex h-full flex-col overflow-hidden p-0">
                  <div className="overflow-hidden">
                    <ResponsiveImage
                      src={s.cover}
                      alt={s.title}
                      loading="lazy"
                      width={1600}
                      height={900}
                      widths={[400, 800]}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                      className="aspect-[16/9] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-center gap-2">
                      <CategoryPill label={s.industry} />
                      <span className="text-xs text-muted-foreground">{formatDate(s.date)}</span>
                    </div>
                    <h3 className="mt-3 line-clamp-3 text-lg font-semibold leading-snug tracking-tight text-primary">
                      {s.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 flex-1 text-base leading-relaxed text-muted-foreground">
                      {s.excerpt}
                    </p>
                    <p className="mt-3 inline-flex items-center text-primary transition group-hover:translate-x-1">
                      <span className="sr-only">Read story</span>
                      <ArrowRight aria-hidden="true" className="h-4 w-4" />
                    </p>
                  </div>
                </GlassCard>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* INSIGHTS */}
      <section id="insights" className="relative bg-stone/40 py-16 md:py-20">
        <Container>
          <h2 className="text-2xl font-semibold tracking-tight text-primary md:text-3xl lg:text-4xl">Featured insights</h2>

          {articles.length > 0 && (
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {articles.slice(0, 3).map((a, i) => (
                <Link
                  key={a.slug}
                  to="/insights/$slug"
                  params={{ slug: a.slug }}
                  className="group h-full"
                >
                  <GlassCard animateIndex={i} className="flex h-full flex-col overflow-hidden p-0">
                    <div className="overflow-hidden">
                      <ResponsiveImage
                        src={a.cover}
                        alt={a.title}
                        loading="lazy"
                        width={1280}
                        height={720}
                        widths={[400, 800]}
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                        className="aspect-[16/9] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <div className="flex items-center gap-2">
                        <CategoryPill label={insightDisplayLabel(a)} />
                        <span className="text-xs text-muted-foreground">{formatDate(a.date)}</span>
                      </div>
                      <h3 className="mt-3 line-clamp-2 text-lg font-semibold leading-snug tracking-tight text-primary">
                        {a.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 flex-1 text-base leading-relaxed text-muted-foreground">
                        {a.excerpt}
                      </p>
                      <p className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary transition group-hover:gap-2">
                        <ArrowRight className="h-3.5 w-3.5" />
                      </p>
                    </div>
                  </GlassCard>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-10">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 rounded-md border border-primary px-5 py-3 text-base font-semibold text-primary transition hover:bg-primary hover:text-primary-foreground"
            >
              View all articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Container>
      </section>

      {/* CONTACT — lighter */}
      <section id="contact" className="relative py-20 md:py-24">
        <Container>
          <div className="grid items-center gap-8 rounded-2xl  bg-card p-8 shadow-[var(--shadow-card)] md:grid-cols-[1.4fr_1fr] md:p-12">
            <div>
              <SectionEyebrow>Contact Us</SectionEyebrow>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-primary md:text-3xl">
                Speak with our team about your tax, audit or advisory needs.
              </h2>
            </div>
            <div className="md:justify-self-end">
              <button
                type="button"
                onClick={() => setEnquiryOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-primary bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground transition hover:bg-primary/90"
              >
                Contact us <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
